import { useState, useEffect } from "react";
import { getFirestoreCollection, subscribeToCollection } from "../lib/firebase";
import { motion, AnimatePresence } from "framer-motion";
import { RippleButton } from "./RippleButton";
import { 
  Droplets, Scissors, Sparkles, Shield, Crown, Check, Heart, 
  Dog, Cat, Footprints, Stethoscope, Award, Clock, ChevronDown
} from "lucide-react";

const SIZES = [
  { id: "small", label: "Small Dog", desc: "Pug, Pomeranian, Shih Tzu" },
  { id: "medium", label: "Medium Dog", desc: "Beagle, Indie, Cocker Spaniel" },
  { id: "large", label: "Large Dog", desc: "Labrador, Golden Retriever, GSD" },
  { id: "puppy", label: "Puppy Special", desc: "Under 6 Months (All Breeds)" },
];

const PACKAGES = [
  {
    id: "dog-senior",
    name: "Senior Pet Care",
    tag: "Gentle & Relaxing Care 👵🐾",
    summary: "Gentle, stress-free bath & relaxing skin care designed for senior dogs.",
    badge: "Dog",
    bannerBg: "bg-[#00a99d]", // SniffyNose Teal
    image: "/Woofsy - Senior Pet Care.png",
    prices: { small: 1799, medium: 1799, large: 1799, puppy: 1799 },
    price: 1799,
    duration: "60-75 mins",
    inclusions: [
      "Sensitive Skin Bath",
      "Paw Care & Hydration",
      "Hygiene Trim",
      "Ear Cleaning & Sanitization",
      "Gentle Teeth Cleaning",
      "Relaxing Anti-Stress Massage",
    ],
  },
  {
    id: "dog-luxury",
    name: "Luxury Spa Experience",
    tag: "Ultra-VIP Pampering 💎",
    summary: "Ultra-VIP spa pampering with coat conditioning, styling & grooming report.",
    badge: "Dog",
    bannerBg: "bg-[#f5a623]", // SniffyNose Amber/Gold
    image: "/Woofsy - Luxury Spa Experience.png",
    prices: { small: 2799, medium: 2799, large: 2799, puppy: 2799 },
    price: 2799,
    duration: "90-105 mins",
    inclusions: [
      "Luxury Spa Hydro-Bath",
      "Custom Hair Styling & Trim",
      "Argan Coat Conditioning",
      "De-Shedding Fur Treatment",
      "Paw Pad Butter Massage",
      "Enzyme Teeth Cleaning",
      "Ear Canal Cleaning",
      "Designer Pet Perfume",
      "Complimentary Bow/Bandana",
      "Official Grooming Report Card",
    ],
  },
  {
    id: "dog-essential",
    name: "Essential Care",
    tag: "Essential Spa Package 🐾",
    summary: "Essential hydro bath, blow dry, ear cleaning & coat brushing.",
    badge: "Dog",
    bannerBg: "bg-[#00a99d]",
    image: "/Woofsy - Essential Care.png",
    prices: { small: 1299, medium: 1299, large: 1299, puppy: 1299 },
    price: 1299,
    duration: "45-60 mins",
    inclusions: [
      "Warm Bath & Blow Dry",
      "Ear Cleaning & Sanitization",
      "Nail Trimming & Smooth Filing",
      "Coat Perfume Spray",
      "Basic Coat De-tangle Brushing",
    ],
  },
  {
    id: "dog-premium",
    name: "Premium Spa",
    tag: "Luxury All-Inclusive Spa",
    summary: "Premium hydro bath, haircut, nail filing & free souvenir bandana.",
    badge: "Dog",
    bannerBg: "bg-[#f5a623]",
    image: "/Woofsy - Luxury Spa Experience.png",
    prices: { small: 1999, medium: 1999, large: 1999, puppy: 1999 },
    price: 1999,
    duration: "75-90 mins",
    inclusions: [
      "Premium Hydro-Spa Bath",
      "Full Haircut & Styling",
      "Nail Trimming & Filing",
      "Ear Sanitization",
      "Teeth Brushing",
      "Paw Pad Hydration Care",
      "Coat Perfume Finish",
      "Free Woofsy Bandana",
    ],
  },
  {
    id: "dog-bath-fluff",
    name: "Bath & Fluff Dry Spa",
    tag: "Essential Grooming",
    summary: "Warm herbal bath, fluff blow-dry, paw massage & perfume spray.",
    badge: "Dog",
    bannerBg: "bg-[#0284c7]", // SniffyNose Sky Blue
    image: "/Woofsy - Bath & Fluff Dry Spa.png",
    prices: { small: 599, medium: 799, large: 999, puppy: 499 },
    price: 799,
    duration: "30-45 mins",
    inclusions: [
      "Warm herbal shampoo bath",
      "Fluff blow-dry & brushing",
      "Ear cleaning & sanitization",
      "Paw pad massage & hydration",
      "Coat perfume spray",
    ],
  },
  {
    id: "dog-full-grooming",
    name: "Full Grooming & Styling",
    tag: "Head-to-Tail Spa",
    summary: "Full body haircut, breed styling, nail clipping & coat shine conditioner.",
    badge: "Dog",
    bannerBg: "bg-[#f5a623]",
    image: "/Woofsy - Full Grooming and Styling.jpeg",
    prices: { small: 1099, medium: 1399, large: 1699, puppy: 899 },
    price: 1399,
    duration: "60-75 mins",
    inclusions: [
      "Full body haircut / breed styling",
      "Herbal bath & condition blow-dry",
      "Nail clipping & filing",
      "Ear & eye cleaning",
      "Sanitary area trim",
      "Coat shine conditioner & perfume",
    ],
  },
  {
    id: "dog-anti-tick",
    name: "Anti-Tick & Flea Treatment",
    tag: "Vet Approved Wash",
    summary: "Medicated anti-tick wash, manual flea removal & soothing preventive spray.",
    badge: "Dog",
    bannerBg: "bg-[#7c3aed]", // Purple
    image: "/Woofsy - Bath & Fluff Dry Spa.png",
    prices: { small: 799, medium: 1099, large: 1299, puppy: 699 },
    price: 1099,
    duration: "45-60 mins",
    inclusions: [
      "Medicated anti-tick wash & dip",
      "Manual tick & flea removal",
      "Soothing anti-itch skin spray",
      "Blow-dry & coat inspection",
      "Anti-tick preventive collar spray",
    ],
  },
  {
    id: "dog-puppy-first",
    name: "Puppy First Groom",
    tag: "Ultra Gentle First Groom 🐶",
    summary: "Ultra gentle low-noise shampoo bath & gentle face/paw trim for puppies.",
    badge: "Dog",
    bannerBg: "bg-[#00a99d]",
    image: "/Woofsy - Puppy First Groom.png",
    prices: { small: 1199, medium: 1199, large: 1199, puppy: 1199 },
    price: 1199,
    duration: "40-45 mins",
    inclusions: [
      "Gentle Puppy Shampoo Bath",
      "Low-Noise Blow Dry",
      "Gentle Face & Paw Trim",
      "Nail Clipping & Filing",
      "Ear Cleaning & Eye Wipe",
      "Soothing Puppy Perfume",
    ],
  },
  {
    id: "dog-royal-spa",
    name: "Royal Ultra-Luxury Spa",
    tag: "VIP Pampering Package",
    summary: "Aromatherapy hydro bath, breed haircut, organic paw butter & souvenir.",
    badge: "Dog",
    bannerBg: "bg-[#f5a623]",
    image: "/Woofsy - Annual VIP Care plan.png",
    prices: { small: 1699, medium: 1899, large: 2199, puppy: 1299 },
    price: 1899,
    duration: "90 mins",
    inclusions: [
      "Aromatherapy hydro bath",
      "Full breed haircut & styling",
      "Fur detangling & dematting",
      "Organic paw butter massage",
      "Teeth brushing & mouth freshener",
      "Sanitary trim & nail filing",
      "Free Woofsy souvenir bandanna",
    ],
  },
];

const CAT_PACKAGES = [
  {
    id: "cat-senior",
    name: "Senior Pet Care",
    tag: "Gentle & Relaxing Care 👵🐾",
    summary: "Gentle cat bath, paw care balm, hygiene trim & soothing massage.",
    badge: "Cat",
    bannerBg: "bg-[#00a99d]",
    image: "/Woofsy - Senior Pet Care.png",
    price: 1799,
    duration: "60-75 mins",
    inclusions: [
      "Sensitive Skin Bath",
      "Paw Care & Balm",
      "Hygiene Area Trim",
      "Ear Canal Sanitization",
      "Teeth Wipe & Freshener",
      "Relaxing Gentle Massage",
    ],
  },
  {
    id: "cat-luxury",
    name: "Luxury Spa Experience",
    tag: "Ultra-VIP Pampering 💎",
    summary: "Luxury hydro cat bath, argan conditioning, paw butter & ribbon.",
    badge: "Cat",
    bannerBg: "bg-[#f5a623]",
    image: "/Woofsy - Luxury Spa Experience.png",
    price: 2799,
    duration: "90-105 mins",
    inclusions: [
      "Luxury Cat Hydro Spa Bath",
      "Soft Coat Hair Styling",
      "Argan Fur Conditioning",
      "De-Shedding Brushing",
      "Paw Pad Butter Massage",
      "Teeth Cleaning",
      "Ear Sanitization",
      "Cat-Safe Perfume Spray",
      "Complimentary Bow/Ribbon",
      "Grooming Progress Card",
    ],
  },
  {
    id: "cat-kitten-first",
    name: "Kitten First Groom",
    tag: "Ultra Gentle Care 🐱",
    summary: "Ultra quiet warm blow dry, face & paw trim with gentle kitten perfume.",
    badge: "Cat",
    bannerBg: "bg-[#00a99d]",
    image: "/Woofsy - Puppy First Groom.png",
    price: 1199,
    duration: "40-45 mins",
    inclusions: [
      "Gentle Kitten Herbal Bath",
      "Ultra Quiet Warm Blow Dry",
      "Face & Paw Trim",
      "Nail Clipping & Filing",
      "Ear Sanitization",
      "Soothing Kitten Perfume",
    ],
  },
  {
    id: "cat-essential",
    name: "Essential Care",
    tag: "Cat Essential Spa 🐾",
    summary: "Bath, low-noise dry, ear sanitization, nail trimming & coat brushing.",
    badge: "Cat",
    bannerBg: "bg-[#0284c7]",
    image: "/Woofsy - Essential Care.png",
    price: 1299,
    duration: "45-60 mins",
    inclusions: [
      "Bath & Low-Noise Dry",
      "Ear Sanitization & Eye Wipe",
      "Nail Trimming & Smooth Filing",
      "Stress-Free Freshener Spray",
      "Basic Fur Brushing",
    ],
  },
  {
    id: "cat-premium",
    name: "Premium Spa",
    tag: "Luxury All-Inclusive Spa",
    summary: "Organic spa bath, custom hair styling, teeth cleaning & paw care.",
    badge: "Cat",
    bannerBg: "bg-[#f5a623]",
    image: "/Woofsy - Luxury Spa Experience.png",
    price: 1999,
    duration: "75-90 mins",
    inclusions: [
      "Premium Organic Spa Bath",
      "Fur Haircut & Custom Styling",
      "Nail Trimming & Filing",
      "Ear & Eye Sanitization",
      "Enzyme Teeth Cleaning",
      "Paw Pad Hydration",
      "Gentle Perfume Spray",
      "Cat Bow / Bandana",
    ],
  },
  {
    id: "cat-lion-cut",
    name: "Lion Cut",
    tag: "Signature Haircut Style",
    summary: "Full body lion haircut (mane & tail tuft) with low-noise drying.",
    badge: "Cat",
    bannerBg: "bg-[#7c3aed]",
    image: "/Woofsy - Full Grooming and Styling.jpeg",
    price: 1899,
    duration: "60-75 mins",
    inclusions: [
      "Full body lion haircut (mane & tail tuft)",
      "Gentle cat bath & low-noise dry",
      "Nail trimming & smooth filing",
      "Ear canal cleaning & sanitization",
      "Soothing anti-irritation skin mist",
    ],
  },
];

const ADDONS = [
  {
    id: "addon-paw-balm",
    name: "Paw Balm Treatment",
    tag: "Natural Paw Butter Therapy 🐾",
    summary: "Nourishing organic paw butter massage for dry, cracked paw pads.",
    badge: "Paw Care",
    bannerBg: "bg-[#00a99d]",
    price: 199,
    image: "/Woofsy - Essential Care.png",
    inclusions: [
      "Organic Healing Paw Butter",
      "Soothing Pad Massage",
      "Dry Pad Hydration Therapy",
    ],
  },
  {
    id: "addon-coat-conditioning",
    name: "Coat Conditioning",
    tag: "Deep Fur Hydration ✨",
    summary: "Deep hydrating argan oil fur mask for smooth, radiant coat texture.",
    badge: "Coat Care",
    bannerBg: "bg-[#f5a623]",
    price: 499,
    image: "/Woofsy - Bath & Fluff Dry Spa.png",
    inclusions: [
      "Argan Oil Fur Conditioning Mask",
      "Deep Fur Detangling Brushing",
      "Silk Gloss Finish",
    ],
  },
  {
    id: "addon-teeth-freshening",
    name: "Teeth Freshening",
    tag: "Fresh Pet Breath 🦷",
    summary: "Enzyme toothpaste brushing & mint spray for fresh pet breath.",
    badge: "Dental Care",
    bannerBg: "bg-[#0284c7]",
    price: 399,
    image: "/Woofsy - Essential Care.png",
    inclusions: [
      "Enzyme Toothpaste Brushing",
      "Plaque Wipe & Sanitization",
      "Mint Freshener Spray",
    ],
  },
  {
    id: "addon-face-trim",
    name: "Face Trim",
    tag: "Gentle Eye & Snout Shaping ✂️",
    summary: "Gentle eye-area & snout hair shaping by expert pet groomer.",
    badge: "Hygiene Trim",
    bannerBg: "bg-[#7c3aed]",
    price: 249,
    image: "/Woofsy - Full Grooming and Styling.jpeg",
    inclusions: [
      "Eye Area Hair Trimming",
      "Muzzle & Chin Scissoring",
      "Gentle Face Shaping",
    ],
  },
  {
    id: "addon-eye-cleaning",
    name: "Eye Cleaning",
    tag: "Tear Stain Removal 👁️",
    summary: "Safe tear stain removal & soothing eye sanitization.",
    badge: "Eye Care",
    bannerBg: "bg-[#00a99d]",
    price: 149,
    image: "/Woofsy - Puppy First Groom.png",
    inclusions: [
      "Herbal Eye Wipe Cleaning",
      "Tear Stain Mask Removal",
      "Soothing Eye Sanitization Solution",
    ],
  },
  {
    id: "addon-deodorizing-spray",
    name: "Deodorizing Spray",
    tag: "Long-Lasting Cologne 🌸",
    summary: "Long-lasting pet-safe aromatic cologne spray finish.",
    badge: "Fragrance",
    bannerBg: "bg-[#f5a623]",
    price: 149,
    image: "/Woofsy - Luxury Spa Experience.png",
    inclusions: [
      "Pet-Safe Organic Fragrance Spray",
      "Long-Lasting Odor Elimination",
      "Coat Refreshing Mist",
    ],
  },
  {
    id: "addon-flea-tick",
    name: "Flea & Tick Treatment",
    tag: "Vet Medicated Dip 🛡️",
    summary: "Vet-approved medicated dip wash & manual parasite removal.",
    badge: "Medicated Care",
    bannerBg: "bg-[#7c3aed]",
    price: 1199,
    image: "/Woofsy - Bath & Fluff Dry Spa.png",
    inclusions: [
      "Medicated Anti-Tick Wash",
      "Manual Parasite Inspection & Removal",
      "Anti-Tick Preventive Collar Spray",
    ],
  },
  {
    id: "addon-bandana",
    name: "Bandana",
    tag: "Designer Accessory 🎀",
    summary: "Stylish washable designer Woofsy pet bandana accessory.",
    badge: "Accessory",
    bannerBg: "bg-[#0284c7]",
    price: 499,
    image: "/Woofsy - Essential Care.png",
    inclusions: [
      "Premium Washable Fabric Bandana",
      "Custom Woofsy Print Design",
      "Comfortable Snap Closure",
    ],
  },
  {
    id: "addon-bow-tie",
    name: "Bow Tie",
    tag: "Party Collar Bow 👔",
    summary: "Premium party bow tie collar accessory for handsome pets.",
    badge: "Accessory",
    bannerBg: "bg-[#f5a623]",
    price: 499,
    image: "/Woofsy - Annual VIP Care plan.png",
    inclusions: [
      "Luxury Satin Collar Bow Tie",
      "Adjustable Elastic Fit",
      "Party Ready Photo Accessory",
    ],
  },
];

const MEMBERSHIP_PLANS = [
  {
    id: "silver",
    name: "Silver Membership",
    tag: "Essential Annual Savings 🥈",
    summary: "10% OFF on all grooming services, priority booking & birthday offer.",
    badge: "Membership",
    bannerBg: "bg-[#64748b]",
    price: 2999,
    displayPrice: "₹2,999",
    period: "/year",
    image: "/Woofsy - Quarterly Care plan.png",
    inclusions: [
      "10% OFF on all grooming & spa services",
      "Priority Slot Booking",
      "Special Birthday Grooming Offer",
      "Exclusive Member-Only Discounts",
    ],
  },
  {
    id: "gold",
    name: "Gold Membership",
    tag: "Premium Perks & Spa Upgrades ⭐",
    summary: "15% OFF on all services, 1 free spa upgrade & free nail trimming.",
    badge: "Membership",
    bannerBg: "bg-[#f5a623]",
    price: 5999,
    displayPrice: "₹5,999",
    period: "/year",
    image: "/Woofsy - Monthly Care Plan.png",
    inclusions: [
      "15% OFF on all grooming & spa services",
      "Priority Slot Booking",
      "1 Complimentary Spa Upgrade",
      "Free Nail Trimming with every visit",
      "Special Birthday Gift & Treat",
      "Early Access to Seasonal Offers",
    ],
  },
  {
    id: "platinum",
    name: "Platinum Membership",
    tag: "VIP Royal Treatment 👑",
    summary: "20% OFF on all services, 2 free grooming sessions & 24/7 VIP support.",
    badge: "Membership",
    bannerBg: "bg-[#7c3aed]",
    price: 9999,
    displayPrice: "₹9,999",
    period: "/year",
    image: "/Woofsy - Annual VIP Care plan.png",
    inclusions: [
      "20% OFF on all grooming & spa services",
      "2 Free Premium Grooming Sessions",
      "Unlimited Priority Booking & Same-Day Slot",
      "VIP Dedicated Customer Support",
      "Deluxe Birthday Hamper & Goodies",
      "Free Seasonal Spa Upgrades",
      "Exclusive Partner Brand Benefits",
    ],
  },
];

const SUITE_SERVICES = [
  { id: "doorstep", title: "Doorstep Dog Grooming", desc: "Mobile Van & At-Home Spa Service with 30-min arrival in Pune.", icon: Droplets, color: "text-woofsy-primary bg-woofsy-primary/10" },
  { id: "walking", title: "Dog Walking & Care", desc: "Daily 30/60 min care sessions with real-time GPS logging.", icon: Footprints, color: "text-sky-500 bg-sky-500/10" },
  { id: "vet", title: "Vet Consult & Vaccines", desc: "Verified doctors at your doorstep for health checkups.", icon: Stethoscope, color: "text-emerald-500 bg-emerald-500/10" },
  { id: "training", title: "Dog Training & Behavior", desc: "Professional obedience & puppy socialization modules.", icon: Award, color: "text-purple-500 bg-purple-500/10" },
];

export default function ServicesGrid({ onBook }) {
  const [activePetType, setActivePetType] = useState("dog");
  const [selectedSize, setSelectedSize] = useState("medium");
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [customServices, setCustomServices] = useState([]);

  useEffect(() => {
    getFirestoreCollection("services").then((list) => {
      if (list && list.length > 0) setCustomServices(list);
    });
    const unsub = subscribeToCollection("services", (list) => {
      if (list && list.length > 0) setCustomServices(list);
    });
    return () => unsub();
  }, []);

  const toggleCard = (id) => {
    setExpandedCardId((prev) => (prev === id ? null : id));
  };

  const switchTab = (type) => {
    setActivePetType(type);
    setExpandedCardId(null);
    setShowAll(false);
  };

  const getActiveList = () => {
    if (customServices.length > 0) {
      const matchingServices = customServices.filter((s) => (s.category || "dog") === activePetType);
      if (matchingServices.length > 0) return matchingServices;
    }
    if (activePetType === "dog") return PACKAGES;
    if (activePetType === "addons") return ADDONS;
    return MEMBERSHIP_PLANS;
  };

  const fullList = getActiveList();
  const displayedList = showAll ? fullList : fullList.slice(0, 6);

  return (
    <section id="services" data-testid="services-section" className="relative py-14 sm:py-20 lg:py-28 px-4 sm:px-8 bg-slate-50/50">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-8 sm:mb-10">
          <h2 className="font-heading font-bold text-2xl sm:text-4xl lg:text-5xl text-slate-900 mb-3">
            Grooming Packages For Every Dog's Needs
          </h2>
          <p className="text-slate-600 text-sm sm:text-lg font-medium">
            From hygiene bath to luxury spa, choose your dog's perfect experience.
          </p>
        </motion.div>

        {/* Category Switcher Tabs - horizontal scroll on mobile */}
        <div className="flex overflow-x-auto pb-2 sm:pb-0 sm:flex-wrap sm:justify-center gap-2.5 sm:gap-3 mb-8 sm:mb-12 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-none" data-testid="pet-type-selector">
          <button
            onClick={() => switchTab("dog")}
            data-testid="tab-dog-grooming"
            className={`flex shrink-0 items-center gap-2 px-4 sm:px-6 py-3 rounded-2xl font-bold text-xs sm:text-sm transition-all duration-300 border ${
              activePetType === "dog"
                ? "bg-woofsy-primary text-white border-woofsy-primary shadow-lg scale-105"
                : "bg-white glass text-woofsy-dark border-woofsy-dark/10 hover:border-woofsy-primary"
            }`}
          >
            <Dog className="w-4 h-4 animate-ear-wiggle" />
            <span>Dog Grooming 🐶</span>
          </button>

          <button
            onClick={() => switchTab("addons")}
            data-testid="tab-addon-services"
            className={`flex shrink-0 items-center gap-2 px-4 sm:px-6 py-3 rounded-2xl font-bold text-xs sm:text-sm transition-all duration-300 border ${
              activePetType === "addons"
                ? "bg-woofsy-primary text-white border-woofsy-primary shadow-lg scale-105"
                : "bg-white glass text-woofsy-dark border-woofsy-dark/10 hover:border-woofsy-primary"
            }`}
          >
            <Sparkles className="w-4 h-4 text-amber-300 animate-spin-slow" />
            <span>Add-On Services ✨</span>
          </button>

          <button
            onClick={() => switchTab("membership")}
            data-testid="tab-membership-plans"
            className={`flex shrink-0 items-center gap-2 px-4 sm:px-6 py-3 rounded-2xl font-bold text-xs sm:text-sm transition-all duration-300 border ${
              activePetType === "membership"
                ? "bg-woofsy-primary text-white border-woofsy-primary shadow-lg scale-105"
                : "bg-white glass text-woofsy-dark border-woofsy-dark/10 hover:border-woofsy-primary"
            }`}
          >
            <Crown className="w-4 h-4 animate-crown-shine text-amber-300" />
            <span>Membership Plans 👑</span>
          </button>
        </div>

        {/* Pricing Cards Grid */}
        <AnimatePresence mode="wait">
          <motion.div key={activePetType + (showAll ? "-all" : "-6")} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8 mb-10 sm:mb-16 items-start">
            {displayedList.map((p, i) => {
              const isExpanded = expandedCardId === p.id;
              const cardPrice = p.prices ? p.prices[selectedSize] : p.price;
              const displayPriceStr = p.displayPrice ? p.displayPrice : `₹${cardPrice}`;

              return (
                <motion.div key={p.id} data-testid={`package-card-${i}`}
                  initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  whileHover={{ y: -6 }}
                  className="bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-2xl border border-slate-200/80 flex flex-col justify-between transition-all duration-300 group">
                  
                  {/* 1. Top Image Banner */}
                  <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-100">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" />
                    
                    {/* Top Right Pill Badge */}
                    <span className="absolute top-3 right-3 text-xs font-bold px-3 py-1 rounded-full bg-white/95 text-slate-800 shadow-md backdrop-blur-md flex items-center gap-1">
                      <span className="text-amber-500 font-extrabold">🐾</span>
                      <span>{p.badge}</span>
                    </span>
                  </div>

                  {/* 2. Middle Colored Summary Strip */}
                  <div className={`px-4 py-2 text-xs font-semibold text-white flex items-center gap-2 min-h-[2.5rem] ${p.bannerBg || 'bg-[#00a99d]'}`}>
                    <Sparkles className="w-3.5 h-3.5 shrink-0" />
                    <span className="leading-tight line-clamp-2">{p.summary || p.tag}</span>
                  </div>

                  {/* 3. Bottom White Card Content */}
                  <div className="p-5 flex flex-col justify-between flex-1">
                    <div>
                      {/* Title & What Includes Link */}
                      <div className="flex items-start justify-between gap-2 mb-2 min-h-[3.25rem]">
                        <h3 className="font-heading font-extrabold text-lg text-slate-900 leading-snug my-auto">{p.name}</h3>
                        <button
                          onClick={() => toggleCard(p.id)}
                          data-testid={`what-includes-${p.id}`}
                          className="text-xs font-bold text-sky-600 hover:text-sky-700 underline shrink-0 whitespace-nowrap flex items-center gap-0.5 transition-colors mt-1"
                        >
                          <span>What Includes?</span>
                          <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
                            <ChevronDown className="w-3.5 h-3.5" />
                          </motion.div>
                        </button>
                      </div>

                      {/* Price & Duration */}
                      <div className="text-xs text-slate-600 font-semibold mb-4">
                        <span>From <strong className="text-base text-slate-900 font-extrabold">{displayPriceStr}</strong></span>
                        {p.period && <span className="text-slate-500 font-medium"> {p.period}</span>}
                        {p.duration && <span className="ml-2 font-normal text-slate-500">· {p.duration}</span>}
                      </div>

                      {/* Expanded Inclusions Dropdown */}
                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mb-4 p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-2 text-xs"
                        >
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Package Inclusions:</p>
                          {p.inclusions.map((inc, idx) => (
                            <div key={idx} className="flex items-start gap-2 text-slate-700 font-medium">
                              <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5 font-bold" />
                              <span className="leading-snug">{inc}</span>
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </div>

                    {/* Book Now Button */}
                    <button
                      data-testid={`package-book-${i}`}
                      onClick={() => onBook(p.name || p.title)}
                      className="w-full py-3.5 rounded-2xl bg-woofsy-primary hover:bg-[#e8792c] text-white font-heading font-extrabold text-sm shadow-md shadow-orange-500/20 hover:shadow-lg hover:shadow-orange-500/30 transition-all text-center"
                    >
                      Book Now
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </AnimatePresence>

        {/* View All / Show Less Toggle Button */}
        {fullList.length > 6 && (
          <div className="flex justify-center -mt-8 mb-16">
            <button
              onClick={() => setShowAll(!showAll)}
              data-testid="toggle-view-all-services"
              className="px-8 py-3.5 rounded-2xl bg-white border-2 border-woofsy-primary/30 hover:border-woofsy-primary text-woofsy-dark font-heading font-extrabold text-sm shadow-sm hover:shadow-md transition-all flex items-center gap-2"
            >
              <span>{showAll ? "Show Less Services" : `View All Services (${fullList.length})`}</span>
              <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${showAll ? "rotate-180" : ""}`} />
            </button>
          </div>
        )}

        {/* Pet Care Suite */}
        <div className="rounded-3xl bg-gradient-to-br from-woofsy-dark via-slate-900 to-slate-950 text-white p-6 sm:p-10 shadow-xl relative overflow-hidden">
          <div className="shimmer-sweep" />

          <div className="relative z-10 max-w-3xl mb-8">
            <span className="inline-block px-3 py-1 rounded-full bg-woofsy-primary/20 text-woofsy-primary font-semibold text-[11px] mb-2 flex items-center gap-1.5 w-fit">
              <Crown className="w-3 h-3 animate-crown-shine text-amber-300" />
              <span>Comprehensive Pet Platform</span>
            </span>
            <h3 className="font-heading font-extrabold text-2xl sm:text-3xl leading-tight">Complete Pet Care Ecosystem in Pune</h3>
            <p className="text-white/70 text-sm mt-1">Just like SniffyNose, Woofsy brings grooming, walking, veterinary care, and training directly to pet parents.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 relative z-10">
            {SUITE_SERVICES.map((suite, i) => {
              const isExpanded = expandedCardId === suite.id;
              return (
                <div key={i} className="bg-white/5 backdrop-blur-md rounded-2xl p-5 border border-white/10 hover:bg-white/10 transition-colors flex flex-col justify-between relative group">
                  <div>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${suite.color}`}>
                      <suite.icon className="w-5 h-5 animate-ear-wiggle" />
                    </div>
                    <h4 className="font-heading font-semibold text-base mb-1">{suite.title}</h4>
                    <p className={`text-white/60 text-xs leading-relaxed transition-all ${isExpanded ? "" : "line-clamp-2"}`}>
                      {suite.desc}
                    </p>
                  </div>

                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={() => toggleCard(suite.id)}
                      data-testid={`down-arrow-suite-${i}`}
                      className="flex items-center gap-1 text-[10px] font-bold text-amber-300 hover:text-white transition-colors px-2 py-0.5 rounded-lg bg-white/10 border border-white/10"
                    >
                      <span>{isExpanded ? "Less" : "Details"}</span>
                      <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
                        <ChevronDown className="w-3.5 h-3.5" />
                      </motion.div>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-white/70">Need a custom pet care schedule or multi-pet discount in Pune?</p>
            <RippleButton variant="primary" className="px-5 py-2.5 text-xs" onClick={onBook}>
              Book Pet Care Now
            </RippleButton>
          </div>
        </div>
      </div>
    </section>
  );
}
